import { Filter } from "@/shared/lib/filter/filter";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { GfsUnitValue } from "@/services/backend/gfs/unit";
import { PestcontrolMonitor } from "@/services/backend/pestcontrol/types/monitor";
import {
  PestGroupSnapshot,
  PestSnapshot,
} from "@/services/backend/pestcontrol/types/pest";

export interface AssetRef {
  fileId: string;
  prefix: string;
  addedAt: string;
}

export interface GfsConsumable {
  id: string;
  name: string;
  type: string;
  unit: GfsUnitValue;
  unitPrice: number;
  productDisplayText?: string;
  pests: PestSnapshot[];
  groups: PestGroupSnapshot[];
}

export function formatGfsConsumableDisplayName(
  consumable: Pick<GfsConsumable, "name" | "productDisplayText">,
) {
  const name = consumable.name.trim();
  const detail = (consumable.productDisplayText ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!detail) return name;
  const abbreviated =
    detail.length > 28 ? `${detail.slice(0, 28).trimEnd()}…` : detail;
  return `${name} (${abbreviated})`;
}

export type GfsMonitorConsumableMode = "REFERENCED" | "INCLUDED";

export interface GfsMonitorConsumable {
  consumableId: string;
  mode: GfsMonitorConsumableMode;
  quantity: number;
  unitPriceOverride?: number;
}

export interface GfsMonitorSnapshot extends Omit<
  PestcontrolMonitor,
  "id" | "createdAt" | "updatedAt"
> {
  sourceId: string;
  takenAt: string;
}

export interface GfsCatalogItem {
  id: string;
  sourceMonitorId?: string;
  monitor: GfsMonitorSnapshot;
  description: string;
  price: number;
  image?: AssetRef;
  pests: PestSnapshot[];
  groups: PestGroupSnapshot[];
  installationTime: number;
  serviceTime: number;
  consumables: GfsMonitorConsumable[];
}

export interface GfsCatalog {
  id: string;
  companyId: string;
  customerId?: string;
  name: string;
  description: string;
  isFallback: boolean;
  items: GfsCatalogItem[];
  consumables: GfsConsumable[];
  createdAt: string;
  updatedAt: string;
}

export interface GfsCatalogListItem {
  id: string;
  companyId: string;
  customerId?: string;
  name: string;
  description: string;
  isFallback: boolean;
  itemCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface GfsCatalogListResponse {
  items: GfsCatalogListItem[];
  count: number;
  offset: number;
  totalCount: number;
}

export interface PutGfsCatalogRequest {
  id: string;
  companyId: string;
  customerId?: string;
  name: string;
  description: string;
  isFallback: boolean;
}

export interface PutGfsCatalogItemRequest {
  catalogId: string;
  itemId: string;
  monitorId?: string;
  name?: string;
  type?: string;
  installationTime: number;
  serviceTime: number;
  consumables: GfsMonitorConsumable[];
}

export interface UpdateGfsCatalogItemRequest {
  catalogId: string;
  item: GfsCatalogItem;
  installationTime: number;
  serviceTime: number;
}

export interface RefreshGfsCatalogItemRequest {
  catalogId: string;
  itemId: string;
}

export interface GfsCatalogItemRefreshPreview {
  current: GfsCatalogItem;
  preview: GfsCatalogItem;
}

export interface UploadGfsCatalogItemImageRequest {
  catalogId: string;
  itemId: string;
  fileId: string;
  file: File | null;
}

export interface DeleteGfsCatalogItemImageRequest {
  catalogId: string;
  itemId: string;
}

export interface ShowGfsCatalogItemImageRequest {
  catalogId: string;
  itemId: string;
  fileId: string;
  filePrefix: string;
}

export interface LoadGfsCatalogRequest {
  id: string;
}

export interface DeleteGfsCatalogRequest {
  id: string;
}

export interface DeleteGfsCatalogItemRequest {
  catalogId: string;
  itemId: string;
}

export interface PutGfsCatalogConsumableRequest {
  catalogId: string;
  consumable: GfsConsumable;
}

export interface DeleteGfsCatalogConsumableRequest {
  catalogId: string;
  consumableId: string;
}

export interface ListGfsCatalogRequest {
  company: Filter<string>;
  customer?: Filter<string>;
  name?: Filter<string>;
  orderBy?: OrdrBy<"name" | "createdAt" | "updatedAt">[];
  limit?: number;
  offset?: number;
}
